/**
 * Service Unavailable Fallback Component
 * 
 * Displays a professional, user-friendly message when:
 * - Backend is unreachable
 * - Critical API calls fail
 * - System is under maintenance
 */

import React from 'react';
import { RefreshCw, AlertCircle, Mail, Clock } from 'lucide-react';

/**
 * Main fallback component for when backend is unavailable
 */
export function ServiceUnavailable({ 
  onRetry, 
  isRetrying = false,
  variant = 'default', // 'default' | 'sales' | 'minimal'
  title = null,
  message = null,
  errorDetail = null,
}) {
  const defaultTitle = "Vi uppdaterar just nu systemet";
  const defaultMessage = "Försök igen om en liten stund.";
  
  const salesTitle = "Vi uppdaterar just nu systemet";
  const salesMessage = "Om du vill kan du prova igen om en stund eller lämna dina uppgifter så återkommer vi.";
  
  const displayTitle = title || (variant === 'sales' ? salesTitle : defaultTitle);
  const displayMessage = message || (variant === 'sales' ? salesMessage : defaultMessage);
  
  return (
    <div className="min-h-[300px] flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#255E69]/10 flex items-center justify-center">
          <Clock className="w-8 h-8 text-[#255E69]" />
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-800 mb-3">
          {displayTitle}
        </h2>
        
        {/* Message */}
        <p className="text-gray-600 mb-6">
          {displayMessage}
        </p>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#255E69] text-white rounded-lg font-medium hover:bg-[#1e4d56] disabled:opacity-50 transition-colors"
              data-testid="retry-button"
            >
              <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Försöker...' : 'Försök igen'}
            </button>
          )}
          
          {variant === 'sales' && (
            <a
              href="mailto:hej@elido.nu"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#255E69] text-[#255E69] rounded-lg font-medium hover:bg-[#255E69]/5 transition-colors"
              data-testid="contact-button"
            >
              <Mail className="w-4 h-4" />
              Kontakta oss
            </a>
          )}
        </div>

        {errorDetail && (
          <details className="mt-6 text-left bg-slate-50 border border-slate-200 rounded-lg p-3">
            <summary className="text-xs text-slate-500 cursor-pointer select-none">
              Visa tekniska detaljer (skicka denna text till support)
            </summary>
            <pre
              className="mt-2 text-[11px] text-slate-700 whitespace-pre-wrap break-words font-mono max-h-64 overflow-auto"
              data-testid="error-boundary-detail"
            >
              {errorDetail}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * Inline error message for smaller components
 */
export function InlineError({ message = "Kunde inte ladda innehållet", onRetry, isRetrying }) {
  return (
    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-lg">
      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
      <span className="text-red-700 text-sm flex-1">{message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          disabled={isRetrying}
          className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${isRetrying ? 'animate-spin' : ''}`} />
          Försök igen
        </button>
      )}
    </div>
  );
}

/**
 * Loading state with timeout fallback
 */
export function LoadingWithFallback({ 
  isLoading, 
  hasError, 
  onRetry,
  children,
  loadingComponent = null,
  errorVariant = 'default'
}) {
  if (hasError) {
    return <ServiceUnavailable onRetry={onRetry} variant={errorVariant} />;
  }
  
  if (isLoading) {
    return loadingComponent || (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#255E69] border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-500 text-sm">Laddar...</span>
        </div>
      </div>
    );
  }
  
  return children;
}

/**
 * Error Boundary for catching React errors
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const detail = err ? `${err.name || "Error"}: ${err.message || String(err)}\n\n${(err.stack || "").slice(0, 1200)}` : "";
      return (
        <ServiceUnavailable 
          onRetry={this.handleRetry}
          variant={this.props.variant || 'default'}
          title="Något gick fel"
          message="Ett oväntat fel uppstod. Försök att ladda om sidan."
          errorDetail={detail}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for managing API state with fallback
 */
export function useApiWithFallback(fetchFn, options = {}) {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  
  const { timeout = 30000, retries = 2 } = options;
  
  const execute = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage(null);
    
    // Create timeout
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
      setErrorMessage('Begäran tog för lång tid');
    }, timeout);
    
    try {
      const result = await fetchFn();
      clearTimeout(timeoutId);
      setData(result);
      setIsLoading(false);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('[useApiWithFallback] Error:', error);
      setHasError(true);
      setErrorMessage(error.message || 'Ett fel uppstod');
      setIsLoading(false);
    }
  }, [fetchFn, timeout]);
  
  React.useEffect(() => {
    execute();
  }, []);
  
  return {
    data,
    isLoading,
    hasError,
    errorMessage,
    retry: execute
  };
}

export default {
  ServiceUnavailable,
  InlineError,
  LoadingWithFallback,
  ErrorBoundary,
  useApiWithFallback
};
